import React, { useEffect, useState, useRef } from "react";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import { formatAmount, formatTotalHours, getAllMonths, getAllYears, getTotalHours } from "../../../../../services/appService";
import { getMonthlyAchSubcontractor, loadMonthlyAchSubcontractor } from "../../../../store/reports";
import GeneratePdf from "../../../../../components/GeneratePdf";

const SubcontractorRecruiter = () => {

    const years = getAllYears();
    const months = getAllMonths();

    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [entityType, setEntityType] = useState("");
    const [pdfShowHide, setPdfShowHide] = useState(false);
    const [selectedCol, setSelectedCol] = useState([]);
    const [selectedColData, setSelectedColData] = useState([]);
    const [subcontractorMonRpt, setSubcontractorMonRpt] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    // Live search, copy tip, and scroll states
    const [tableSearchTerm, setTableSearchTerm] = useState("");
    const [copiedText, setCopiedText] = useState("");
    const [tableWidth, setTableWidth] = useState(0);
    const topScrollRef = useRef(null);
    const tableWrapperRef = useRef(null);

    const achTypes = [
        { id: 1, name: "Sub-Contractors", pmId: 3, pmType: "subcontractor_3" },
        { id: 2, name: "Recruiter", pmId: 5, pmType: "recruiter_5" },
        { id: 3, name: "W2 Candidate", pmId: 69, pmType: "candidate_69" },
        { id: 4, name: "1099 Candidate", pmId: 70, pmType: "candidate_70" },
    ];

    const monthlyAchSubcontractorSelector = useSelector(getMonthlyAchSubcontractor);
    const dispatch = useDispatch();

    useEffect(() => {
        setSubcontractorMonRpt(monthlyAchSubcontractorSelector || []);
        setIsLoading(false);
    }, [monthlyAchSubcontractorSelector]);

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedText(text);
        setTimeout(() => setCopiedText(""), 2000);
    };

    const handleEntityTypeSelect = (type) => {
        setEntityType(type);
        setSubcontractorMonRpt([]);
        setHasFetched(false);
    };

    const handleYearChange = (val) => {
        setYear(val);
        setSubcontractorMonRpt([]);
        setHasFetched(false);
    };

    const handleMonthChange = (val) => {
        setMonth(val);
        setSubcontractorMonRpt([]);
        setHasFetched(false);
    };

    const handleGenerateRpt = () => {
        if (year && month && entityType) {
            setIsLoading(true);
            setHasFetched(true);
            dispatch(loadMonthlyAchSubcontractor(year, month, entityType));
            setTableSearchTerm("");
        }
    };

    const getFilteredReports = () => {
        if (!subcontractorMonRpt) return [];
        return subcontractorMonRpt.filter(item => {
            const term = tableSearchTerm.toLowerCase();
            const companyName = (item.name || "").toLowerCase();
            const candidateName = ((item.candidate || "") + " " + (item.lastName || "")).toLowerCase();
            const bankName = (item.bank || "").toLowerCase();
            const clientName = (item.client || "").toLowerCase();
            const invoiceNo = (item.invoice || "").toLowerCase();
            const email = (item.achEmail || "").toLowerCase();

            return companyName.includes(term) ||
                candidateName.includes(term) ||
                bankName.includes(term) ||
                clientName.includes(term) ||
                invoiceNo.includes(term) ||
                email.includes(term);
        });
    };

    const filteredReports = getFilteredReports();

    // Stats calculations
    const getKpiStats = () => {
        if (!filteredReports || filteredReports.length === 0) {
            return { totalPaid: "$0.00", totalCandidates: 0, totalHours: 0, totalInvoices: 0 };
        }
        let totalVal = 0;
        let totalHours = 0;
        const uniqueCandidates = new Set();
        const invoices = new Set();

        filteredReports.forEach((item) => {
            totalVal += item.amount || 0;
            const hrs = item.hours || 0;
            const mins = item.minutes || 0;
            totalHours += hrs + (mins / 60);

            uniqueCandidates.add(item.candidate + " " + item.lastName);
            if (item.invoice) invoices.add(item.invoice);
        });

        return {
            totalPaid: "$" + formatAmount(totalVal),
            totalCandidates: uniqueCandidates.size,
            totalHours: Math.round(totalHours * 100) / 100,
            totalInvoices: invoices.size
        };
    };

    const { totalPaid, totalCandidates, totalHours, totalInvoices } = getKpiStats();

    const subcontMonthTotal = () => {
        let total = 0;
        filteredReports.forEach((item) => {
            total = total + item.amount;
        });
        return formatAmount(total);
    };

    const subcontractorExportPDF = () => {
        const headers = [
            [
                "Subcontractor",
                "Candidate",
                "StartDate",
                "Client",
                "Invoice #",
                "Hours",
                "Rate/Hour",
                "Amount $",
                "Bank",
                "Routing #",
                "Account #",
                "Contact Email",
            ],
        ];
        let data = [];
        filteredReports.map((item) =>
            data.push([
                item.name,
                item.candidate,
                item.startDate,
                item.client,
                item.invoice,
                getTotalHours(item.workingHours),
                item.rate,
                formatAmount(getTotalHours(item.workingHours) * item.rate),
                item.bank,
                item.rounting,
                item.account,
                item.achEmail,
            ])
        );
        setSelectedCol(headers);
        setSelectedColData(data);
        setPdfShowHide(true);
    };

    const handlepdfShowHide = () => {
        setPdfShowHide(false);
        setSelectedCol([]);
        setSelectedColData([]);
    };

    // Dual scrollbars synchronization
    const handleTopScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            tableWrapperRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };

    const handleTableScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            topScrollRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
        }
    };

    useEffect(() => {
        const updateWidth = () => {
            const table = document.getElementById("sub_ach_pay");
            if (table) {
                setTableWidth(table.scrollWidth);
            }
        };

        updateWidth();
        const timer = setTimeout(updateWidth, 500);

        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            clearTimeout(timer);
        };
    }, [filteredReports, entityType]);

    const renderClipboardIcon = (val) => {
        if (!val) return null;
        return copiedText === val ? (
            <i className="fa fa-check text-success ach-copy-btn" title="Copied!"></i>
        ) : (
            <i
                className="fa fa-clipboard ach-copy-btn"
                title="Copy coordinates"
                onClick={() => copyToClipboard(val)}
            ></i>
        );
    };

    return (
        <div className="ach-pay-container">
            <style>{`
                .ach-pay-container {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    color: #1e293b;
                    margin-bottom: 2rem;
                }

                /* Visual selector cards */
                .ach-pay-card-selector {
                    margin-bottom: 24px;
                }

                .ach-pay-select-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 16px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    height: 100%;
                    position: relative;
                }

                .ach-pay-select-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.04);
                    border-color: #cbd5e1;
                }

                .ach-pay-select-card.active {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
                    border-width: 2px;
                    margin: -1px;
                }

                .ach-pay-select-card-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }

                .ach-pay-select-card:hover .ach-pay-select-card-icon {
                    transform: scale(1.05);
                }

                .ach-pay-select-card-title {
                    font-size: 13.5px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 2px;
                }

                .ach-pay-select-card-desc {
                    font-size: 11px;
                    color: #64748b;
                    line-height: 1.3;
                }

                /* KPI Metric cards */
                .ach-pay-kpi-row {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .ach-pay-metric-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 14px 18px;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.01);
                }

                .ach-pay-metric-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    margin-right: 14px;
                    background: #f1f5f9;
                    color: #475569;
                    flex-shrink: 0;
                }

                .ach-pay-metric-val {
                    font-size: 18px;
                    font-weight: 800;
                    color: #0f172a;
                    line-height: 1.1;
                    margin-bottom: 2px;
                }

                .ach-pay-metric-label {
                    font-size: 10.5px;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }

                /* Controls Layout */
                .ach-pay-controls {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                    background-color: #fafbfc;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 12px 18px;
                }

                .ach-pay-controls-group {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .ach-pay-search-wrapper {
                    position: relative;
                    width: 240px;
                }

                .ach-pay-search-input {
                    width: 100%;
                    padding: 8px 12px 8px 34px;
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    font-size: 12.5px;
                    color: #1e293b;
                    transition: all 0.2s ease;
                }

                .ach-pay-search-input:focus {
                    outline: none;
                    border-color: #4f46e5;
                    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
                }

                .ach-pay-search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    font-size: 12px;
                }

                .ach-pay-filter-select {
                    height: 35px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    padding: 0 10px;
                    font-size: 12.5px;
                    color: #475569;
                    background: #ffffff;
                }

                .ach-pay-filter-select:focus {
                    outline: none;
                    border-color: #4f46e5;
                }

                .ach-pay-actions-btns {
                    display: flex;
                    align-items: center;
                }

                .ach-pay-actions-btns > * {
                    margin-right: 8px !important;
                }

                .ach-pay-actions-btns > *:last-child {
                    margin-right: 0 !important;
                }

                .ach-pay-btn {
                    height: 35px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 0 14px;
                    border-radius: 8px;
                    font-size: 12.5px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    color: #475569;
                }

                .ach-pay-btn:hover:not(:disabled) {
                    background-color: #f8fafc;
                    color: #0f172a;
                    border-color: #94a3b8;
                }

                .ach-pay-btn:disabled {
                    background-color: #f1f5f9;
                    border-color: #e2e8f0;
                    color: #94a3b8;
                    cursor: not-allowed;
                }

                .ach-pay-btn-primary {
                    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%) !important;
                    color: #ffffff !important;
                    border: 1px solid #4338ca !important;
                    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.15);
                    transition: all 0.2s ease;
                    padding: 0 16px;
                }

                .ach-pay-btn-primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #4338ca 0%, #3730a3 100%) !important;
                    border-color: #3730a3 !important;
                    box-shadow: 0 6px 16px rgba(79, 70, 229, 0.25);
                    transform: translateY(-1px);
                }

                .ach-pay-btn-primary:disabled {
                    background: #f1f5f9 !important;
                    border-color: #e2e8f0 !important;
                    color: #94a3b8 !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                }

                /* Table modifications */
                .ach-pay-table-wrapper {
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    overflow-x: auto;
                    overflow-y: hidden;
                    background: white;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.01);
                }

                .ach-pay-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                    text-align: left;
                }

                .ach-pay-table th {
                    background-color: #f8fafc;
                    color: #475569;
                    font-weight: 700;
                    text-transform: uppercase;
                    font-size: 10.5px;
                    letter-spacing: 0.05em;
                    padding: 12px 16px;
                    border-bottom: 2px solid #e2e8f0;
                    white-space: nowrap;
                }

                .ach-pay-table td {
                    padding: 12px 16px;
                    border-bottom: 1px solid #f1f5f9;
                    color: #334155;
                    transition: background-color 0.15s ease;
                    white-space: nowrap;
                }

                .ach-pay-table tr:hover td {
                    background-color: #f8fafc;
                }

                /* Enhanced Rate and Amount column styling */
                .ach-pay-table td.col-rate {
                    color: #475569;
                    font-weight: 500;
                    text-align: right !important;
                }

                .ach-pay-table td.col-amount {
                    color: #0f172a;
                    font-weight: 600;
                    background-color: rgba(79, 70, 229, 0.015);
                    text-align: right !important;
                }

                /* Total Payment row layout */
                .ach-pay-total-row {
                    background-color: rgba(79, 70, 229, 0.04) !important;
                    border-top: 2px solid #cbd5e1;
                    border-bottom: 2px solid #cbd5e1;
                }

                .ach-pay-total-row td {
                    color: #0f172a !important;
                    font-weight: 700 !important;
                    padding: 14px 16px !important;
                }

                .ach-pay-total-label {
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    font-size: 11px;
                    color: #475569 !important;
                    text-align: right !important;
                }

                .ach-pay-total-value {
                    font-size: 13.5px;
                    color: #4f46e5 !important;
                    font-weight: 800 !important;
                    background-color: rgba(79, 70, 229, 0.04) !important;
                    text-align: right !important;
                }

                /* Copy button */
                .ach-copy-btn {
                    margin-left: 8px;
                    color: #94a3b8;
                    cursor: pointer;
                    font-size: 11px;
                    transition: color 0.15s ease;
                }

                .ach-copy-btn:hover {
                    color: #4f46e5;
                }

                /* Shimmer and Loading Animation */
                @keyframes shimmer {
                  0%   { background-position: -600px 0; }
                  100% { background-position: 600px 0; }
                }

                @keyframes loadingBar {
                  0%   { width: 0%; opacity: 1; }
                  70%  { width: 85%; opacity: 1; }
                  100% { width: 100%; opacity: 0; }
                }

                .wf-loading-bar {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 3px;
                  background: linear-gradient(90deg, #4f46e5, #0ea5e9, #4f46e5);
                  background-size: 200% 100%;
                  animation: loadingBar 1.6s ease-in-out infinite;
                  z-index: 10;
                }

                .skeleton-cell {
                  display: block;
                  height: 12px;
                  border-radius: 6px;
                  background: linear-gradient(
                    90deg,
                    #f1f5f9 25%,
                    #e2e8f0 50%,
                    #f1f5f9 75%
                  );
                  background-size: 600px 100%;
                  animation: shimmer 1.4s ease-in-out infinite;
                }

                .skeleton-cell.sk-short  { width: 40%; }
                .skeleton-cell.sk-medium { width: 65%; }
                .skeleton-cell.sk-long   { width: 85%; }

                .skeleton-row td {
                  padding: 14px 16px !important;
                }

                /* Enhanced empty state */
                .wf-empty-state {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 50px 24px;
                  text-align: center;
                  gap: 12px;
                }

                .wf-empty-icon {
                  width: 52px;
                  height: 52px;
                  border-radius: 14px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 22px;
                  background: linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(14, 165, 233, 0.06) 100%);
                  border: 1px solid rgba(79, 70, 229, 0.12);
                  color: #4f46e5;
                  margin-bottom: 4px;
                }

                .wf-empty-title {
                  font-size: 15px;
                  font-weight: 700;
                  color: #1e293b;
                  margin: 0;
                  width: 100%;
                  text-align: center;
                }

                .wf-empty-desc {
                  font-size: 13px;
                  color: #64748b;
                  margin: 0 auto;
                  line-height: 1.6;
                  text-align: center;
                  width: 100%;
                }
            `}</style>

            <div className="ach-pay-card-selector">
                <div className="row">
                    {achTypes.map((t) => {
                        const isActive = entityType === t.pmType;
                        const icons = {
                            "subcontractor_3": "fa-building-o",
                            "recruiter_5": "fa-user",
                            "candidate_69": "fa-user-circle-o",
                            "candidate_70": "fa-user-md"
                        };
                        const colors = {
                            "subcontractor_3": "rgba(6, 182, 212, 0.08)",
                            "recruiter_5": "rgba(99, 102, 241, 0.08)",
                            "candidate_69": "rgba(16, 185, 129, 0.08)",
                            "candidate_70": "rgba(239, 68, 68, 0.08)"
                        };
                        const borderColors = {
                            "subcontractor_3": "#06b6d4",
                            "recruiter_5": "#4f46e5",
                            "candidate_69": "#10b981",
                            "candidate_70": "#ef4444"
                        };
                        const activeColor = borderColors[t.pmType];

                        return (
                            <div className="col-md-3 mb-3" key={t.id}>
                                <div
                                    className={`ach-pay-select-card ${isActive ? "active" : ""}`}
                                    onClick={() => handleEntityTypeSelect(t.pmType)}
                                    style={isActive ? { borderColor: activeColor, background: colors[t.pmType], borderWidth: "2px" } : {}}
                                >
                                    <div
                                        className="ach-pay-select-card-icon"
                                        style={{
                                            background: colors[t.pmType],
                                            color: borderColors[t.pmType]
                                        }}
                                    >
                                        <i className={`fa ${icons[t.pmType]}`}></i>
                                    </div>
                                    <div className="ach-pay-select-card-body" style={{ flexGrow: 1, minWidth: 0 }}>
                                        <div className="ach-pay-select-card-title">{t.name}</div>
                                        <div className="ach-pay-select-card-desc">
                                            {t.pmType === "subcontractor_3" && "Monthly ACH for Sub-Contractors."}
                                            {t.pmType === "recruiter_5" && "Monthly ACH for Recruiters."}
                                            {t.pmType === "candidate_69" && "Monthly ACH for W2 staff."}
                                            {t.pmType === "candidate_70" && "Monthly ACH for 1099 contractors."}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {entityType !== "" && (
                <div className="ach-pay-controls">
                    <div className="ach-pay-controls-group">
                        <div className="ach-pay-search-wrapper">
                            <i className="fa fa-search ach-pay-search-icon"></i>
                            <input
                                type="text"
                                placeholder="Search by candidate, bank, email..."
                                className="ach-pay-search-input"
                                value={tableSearchTerm}
                                onChange={(e) => setTableSearchTerm(e.target.value)}
                                disabled={subcontractorMonRpt.length === 0}
                            />
                        </div>

                        <select
                            name="year"
                            id="year"
                            className="ach-pay-filter-select"
                            onChange={({ currentTarget }) => handleYearChange(currentTarget.value)}
                            value={year}
                        >
                            <option value="">Year</option>
                            {years.map((y) => (
                                <option key={y.name} value={y.name}>{y.name}</option>
                            ))}
                        </select>

                        <select
                            name="month"
                            id="month"
                            className="ach-pay-filter-select"
                            onChange={({ currentTarget }) => handleMonthChange(currentTarget.value)}
                            value={month}
                        >
                            <option value="">Month</option>
                            {months.map((m) => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                        </select>

                        <button
                            type="button"
                            className="ach-pay-btn ach-pay-btn-primary"
                            disabled={!year || !month || !entityType}
                            onClick={handleGenerateRpt}
                        >
                            <i className="fa fa-refresh" style={{ fontSize: "11px", marginRight: "6px" }}></i>
                            Generate Report
                        </button>
                    </div>

                    <div className="ach-pay-actions-btns">
                        <ReactHTMLTableToExcel
                            id="subcontractorTable"
                            className="ach-pay-btn"
                            table="sub_ach_pay"
                            filename="Subcontractor_ACH_Payment"
                            sheet="Subcontractor_ACH_Payment"
                            buttonText="Excel Export"
                        />
                        <button
                            type="button"
                            className="ach-pay-btn"
                            disabled={filteredReports.length === 0}
                            onClick={subcontractorExportPDF}
                        >
                            <i className="fa fa-file-pdf-o" style={{ color: "#ef4444" }}></i>
                            Export PDF
                        </button>
                    </div>
                </div>
            )}

            {entityType === "" ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-info-circle"></i>
                    </div>
                    <p className="wf-empty-title">No Category Selected</p>
                    <p className="wf-empty-desc">Select a payment recipient category above to inspect monthly ledger details.</p>
                </div>
            ) : (!year || !month) ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-calendar-o"></i>
                    </div>
                    <p className="wf-empty-title">No Date Chosen</p>
                    <p className="wf-empty-desc">Please choose a valid Year and Month in the controls above.</p>
                </div>
            ) : (
                <div className="ach-pay-report-body p-0">
                    {/* KPI Metrics */}
                    {subcontractorMonRpt.length > 0 && (
                        <div className="ach-pay-kpi-row" style={{ marginTop: "16px" }}>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: "rgba(99, 102, 241, 0.08)", color: "#4f46e5" }}>
                                    <i className="fa fa-usd"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalPaid}</div>
                                    <div className="ach-pay-metric-label">Total Amount</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: "rgba(16, 185, 129, 0.08)", color: "#10b981" }}>
                                    <i className="fa fa-users"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalCandidates}</div>
                                    <div className="ach-pay-metric-label">Unique Recipients</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: "rgba(6, 182, 212, 0.08)", color: "#06b6d4" }}>
                                    <i className="fa fa-clock-o"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalHours}h</div>
                                    <div className="ach-pay-metric-label">Total Hours</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon" style={{ background: "rgba(239, 68, 68, 0.08)", color: "#ef4444" }}>
                                    <i className="fa fa-file-text-o"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalInvoices}</div>
                                    <div className="ach-pay-metric-label">Total Invoices</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Synchronized top scrollbar */}
                    {subcontractorMonRpt.length > 0 && filteredReports.length > 0 && (
                        <div
                            ref={topScrollRef}
                            onScroll={handleTopScroll}
                            style={{ overflowX: "auto", overflowY: "hidden", height: "14px", width: "100%", marginBottom: "4px" }}
                        >
                            <div style={{ width: `${tableWidth}px`, height: "1px" }}></div>
                        </div>
                    )}

                    <div
                        ref={tableWrapperRef}
                        onScroll={handleTableScroll}
                        className="ach-pay-table-wrapper"
                        style={{ position: "relative" }}
                    >
                        {isLoading && <div className="wf-loading-bar" />}

                        {isLoading ? (
                            /* SKELETON TABLE LOADING SCREEN */
                            <table className="ach-pay-table">
                                <thead>
                                    <tr>
                                        {entityType === "subcontractor_3" && <th>Subcontractor</th>}
                                        {entityType === "recruiter_5" && <th>Recruiter</th>}
                                        <th>Candidate</th>
                                        <th>Start Date</th>
                                        <th>Client</th>
                                        <th>Invoice #</th>
                                        <th>Hours</th>
                                        <th className="text-right">Rate/hour $</th>
                                        <th className="text-right">Amount $</th>
                                        <th>Bank</th>
                                        <th>Routing #</th>
                                        <th>Account #</th>
                                        <th>Contact Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <tr key={`skel-${i}`} className="skeleton-row">
                                            {entityType === "subcontractor_3" && <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>}
                                            {entityType === "recruiter_5" && <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>}
                                            <td><span className="skeleton-cell sk-long" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-long" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : subcontractorMonRpt.length > 0 ? (
                            filteredReports.length > 0 ? (
                                <table
                                    className="ach-pay-table"
                                    id="sub_ach_pay"
                                >
                                    <thead>
                                        <tr>
                                            {entityType === "subcontractor_3" && <th>Subcontractor</th>}
                                            {entityType === "recruiter_5" && <th>Recruiter</th>}
                                            <th>Candidate</th>
                                            <th>Start Date</th>
                                            <th>Client</th>
                                            <th>Invoice #</th>
                                            <th>Hours</th>
                                            <th className="text-right">Rate/hour $</th>
                                            <th className="text-right">Amount $</th>
                                            <th>Bank</th>
                                            <th>Routing #</th>
                                            <th>Account #</th>
                                            <th>Contact Email</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReports.map((item, index) => (
                                            <tr key={`${item.invoice}-${index}`}>
                                                {entityType === "subcontractor_3" && <td>{item.name}</td>}
                                                {entityType === "recruiter_5" && <td>{item.name}</td>}

                                                <td>{item.candidate}&nbsp;{item.lastName}</td>
                                                <td>
                                                    <Moment format="MMM Do, YYYY">
                                                        {item.startDate}
                                                    </Moment>
                                                </td>
                                                <td>{item.client}</td>
                                                <td>{item.invoice}</td>
                                                <td className="col-hours" style={{ whiteSpace: "nowrap" }}>
                                                    <i className="fa fa-clock-o text-muted" style={{ marginRight: "6px", fontSize: "11.5px" }}></i>
                                                    <span style={{ fontWeight: "600", color: "#1e293b" }}>{item.hours}</span>
                                                    <span style={{ color: "#64748b", fontSize: "11px", marginLeft: "1px", marginRight: "5px" }}>h</span>
                                                    <span style={{ fontWeight: "600", color: "#1e293b" }}>{item.minutes}</span>
                                                    <span style={{ color: "#64748b", fontSize: "11px", marginLeft: "1px" }}>m</span>
                                                </td>
                                                <td className="col-rate text-right">{"$" + formatAmount(item.rate)}</td>
                                                <td className="col-amount text-right">{"$" + formatAmount(item.amount)}</td>
                                                <td>{item.bank}</td>
                                                <td>
                                                    {item.rounting}
                                                    {renderClipboardIcon(item.rounting)}
                                                </td>
                                                <td>
                                                    {item.account}
                                                    {renderClipboardIcon(item.account)}
                                                </td>
                                                <td>
                                                    {item.achEmail}
                                                    {renderClipboardIcon(item.achEmail)}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="ach-pay-total-row">
                                            <td colSpan={entityType === "subcontractor_3" || entityType === "recruiter_5" ? 7 : 6} className="text-right ach-pay-total-label">
                                                Total Payments
                                            </td>
                                            <td className="text-right ach-pay-total-value">
                                                {"$" + subcontMonthTotal()}
                                            </td>
                                            <td colSpan={4}></td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <div className="wf-empty-state">
                                    <div className="wf-empty-icon">
                                        <i className="fa fa-search"></i>
                                    </div>
                                    <p className="wf-empty-title">No Matching Records</p>
                                    <p className="wf-empty-desc">No payment entries matched your search query. Try adjusting your search term.</p>
                                </div>
                            )
                        ) : !hasFetched ? (
                            <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                                <div className="wf-empty-icon">
                                    <i className="fa fa-play-circle-o" style={{ color: "#4f46e5" }}></i>
                                </div>
                                <p className="wf-empty-title">Ready to Load Data</p>
                                <p className="wf-empty-desc">Click the "Generate Report" button above to fetch ACH payment records for this selection.</p>
                            </div>
                        ) : (
                            <div className="wf-empty-state">
                                <div className="wf-empty-icon">
                                    <i className="fa fa-folder-open-o"></i>
                                </div>
                                <p className="wf-empty-title">No Payments Found</p>
                                <p className="wf-empty-desc">No ACH payment records found for this category and date range.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <GeneratePdf
                handlepdfShowHide={handlepdfShowHide}
                pdfShowHide={pdfShowHide}
                selectedCol={selectedCol}
                selectedColData={selectedColData}
            ></GeneratePdf>
        </div>
    );
};

export default SubcontractorRecruiter;